import { Letter as LetterData, Tray } from "../apis/api";
import { findNextEmptySpace } from "../utils";
import { castDraft, produce } from "immer";

export interface CoordinateData {
    location: "GRID" | "TRAY";
    index: number;
}

export type PlayableLetterData = LetterData & {
    readonly tray_index: number;
    readonly grid_index: number | null;

    // gets set by the UI for blank tiles, and we compare it against this.text to see if we should move the tile on the
    // click, or just allow editing
    readonly last_blank_value?: string;
};
export type HighlightableLetterData = LetterData & { highlight: boolean };

export interface GridArrowData {
    readonly direction: "RIGHT" | "DOWN";
    readonly position: number;
}

export type UIState = {
    gridArrow: GridArrowData | null;
    playerLetters: PlayableLetterData[];
    currentClickIndex: number | null;
};

export type UIStateAction =
    | {
          $type: "CLICK";
          location: CoordinateData;
      }
    | {
          $type: "SET_BLANK_LETTER";
          index: number;
          value: string;
      }
    | {
          $type: "RETRIEVE";
      }
    | {
          $type: "DRAG";
          from: CoordinateData;
          to: CoordinateData;
      }
    | {
          $type: "NEW_LETTERS";
          tray: Tray;
      };

function letterMatchingLocation(
    x: CoordinateData,
    playerLetters: (PlayableLetterData | null)[],
): PlayableLetterData | null {
    for (const letter of playerLetters) {
        if (
            letter != null &&
            ((x.location === "GRID" && x.index === letter.grid_index) ||
                (letter.grid_index == null &&
                    x.location === "TRAY" &&
                    x.index === letter.tray_index))
        ) {
            return letter;
        }
    }

    return null;
}

function trayTileAtIndex(
    x: number,
    playerLetters: (PlayableLetterData | null)[],
): PlayableLetterData | null {
    return playerLetters
        .filter((letter) => {
            return letter != null && letter.tray_index == x;
        })
        .pop();
}

export function getUIStateReducer(
    boardLetters: HighlightableLetterData[],
    setConfirmedScorePoints: (x: number) => void,
): (state: UIState, update: UIStateAction) => UIState {
    function uiStateReducer(state: UIState, update: UIStateAction): UIState {

        return produce(state, (draft) => {
            if (update.$type === "SET_BLANK_LETTER") {
                const blankLetter = castDraft(
                    trayTileAtIndex(update.index, draft.playerLetters),
                );
                blankLetter.last_blank_value = blankLetter.text;
                draft.currentClickIndex = null;

                if (blankLetter.text !== update.value) {
                    blankLetter.text = update.value;
                    if (blankLetter.grid_index != null) {
                        setConfirmedScorePoints(-1);
                    }
                }
            } else if (update.$type === "RETRIEVE") {
                setConfirmedScorePoints(-1);
                draft.gridArrow = null;
                draft.currentClickIndex = null;
                for (let letter of draft.playerLetters) {
                    if (letter != null) {
                        letter.grid_index = null;
                    }
                }
            } else if (update.$type === "DRAG") {
                // first we need to check if there's a tile at the to-location
                let to_letter = castDraft(
                    letterMatchingLocation(update.to, draft.playerLetters),
                );
                let from_letter = castDraft(
                    letterMatchingLocation(update.from, draft.playerLetters),
                );

                if (from_letter == null) {
                    // I've observed on Macs that occasionally a 'click' type can also register a second, drag and drop event on the same tile square pointing to itself.
                    // The problem though is that after the first event is processed, for the second event we end up with a null from_letter
                    // If this happens, just cancel the event.
                    return;
                }

                if (to_letter != null) {
                    const any_on_grid =
                        from_letter.grid_index != null ||
                        to_letter.grid_index != null;
                    const any_in_tray =
                        from_letter.grid_index == null ||
                        to_letter.grid_index == null;

                    // Swap grid index if either letter is in the grid
                    if (any_on_grid) {
                        const intermediate = from_letter.grid_index;
                        from_letter.grid_index = to_letter.grid_index;
                        to_letter.grid_index = intermediate;
                        setConfirmedScorePoints(-1);
                    }

                    if (any_in_tray) {
                        // at least one of them are in the tray so we'll also swap tray_index
                        // Note - this if-branch is guaranteed to trigger if the one above didn't
                        // but this branch may also trigger in addition to the one above
                        const intermediate = from_letter.tray_index;
                        from_letter.tray_index = to_letter.tray_index;
                        to_letter.tray_index = intermediate;
                    }
                } else {
                    // set based on the coordinate data
                    if (update.to.location === "GRID") {
                        from_letter.grid_index = update.to.index;
                        setConfirmedScorePoints(-1);
                    } else {
                        // we're dragging a letter into an empty tray spot
                        // but because of how we looked to see if there was a tile here, it's possible
                        // that there's a tile with this destination tray_index that's sitting on the grid
                        // If this tile exists, we need to sway it's tray_index
                        let movedGridLetter = castDraft(
                            trayTileAtIndex(
                                update.to.index,
                                draft.playerLetters,
                            ),
                        );

                        if (movedGridLetter != null) {
                            movedGridLetter.tray_index = from_letter.tray_index;
                        }
                        from_letter.tray_index = update.to.index;

                        if (from_letter.grid_index != null) {
                            setConfirmedScorePoints(-1);
                            from_letter.grid_index = null;
                        }
                    }
                }

                // invalidate any selections
                draft.currentClickIndex = null;
            } else if (update.$type === "CLICK") {
                // first let's see if there's a letter at this location
                const target_letter = castDraft(
                    letterMatchingLocation(
                        update.location,
                        draft.playerLetters,
                    ),
                );

                if (target_letter != null) {
                    // if it's a blank letter and the latest value doesn't match the current value; update the latest and cancel
                    // the event
                    if (
                        target_letter.is_blank &&
                        target_letter.text != target_letter.last_blank_value
                    ) {
                        target_letter.last_blank_value = target_letter.text;
                        return draft;
                    } else {
                        target_letter.last_blank_value = undefined;
                    }

                    // Is it in the tray?
                    if (target_letter.grid_index != null) {
                        // It's in the grid; send back to tray
                        target_letter.grid_index = null;
                        setConfirmedScorePoints(-1);
                        draft.currentClickIndex = null;

                        // Do a check; is the arrow one over from here? We can reverse its direction
                        if (draft.gridArrow != null) {
                            const proposed_reverse_position =
                                findNextEmptySpace(
                                    draft.gridArrow.position,
                                    draft.gridArrow.direction,
                                    true,
                                    boardLetters,
                                    draft.playerLetters,
                                );
                            if (
                                proposed_reverse_position ==
                                update.location.index
                            ) {
                                // reverse the arrow
                                draft.gridArrow.position =
                                    proposed_reverse_position;
                            }
                        }
                    } else {
                        // letter is in the tray; check if there's an arrow
                        if (draft.gridArrow != null) {
                            // arrow is present; set the tile's grid position and update arrow
                            target_letter.grid_index = draft.gridArrow.position;
                            setConfirmedScorePoints(-1);
                            draft.currentClickIndex = null;

                            // Update arrow
                            const new_arrow_position = findNextEmptySpace(
                                draft.gridArrow.position,
                                draft.gridArrow.direction,
                                false,
                                boardLetters,
                                draft.playerLetters,
                            );
                            if (new_arrow_position != null) {
                                draft.gridArrow.position = new_arrow_position;
                            } else {
                                draft.gridArrow = null;
                            }
                        } else {
                            // arrow is not present; mark this one tile for movement
                            draft.currentClickIndex = target_letter.tray_index;
                        }
                    }
                } else if (update.location.location === "GRID") {
                    // We clicked a blank spot on the grid; do we have a letter currently selected?
                    if (draft.currentClickIndex != null) {
                        castDraft(
                            trayTileAtIndex(
                                draft.currentClickIndex,
                                draft.playerLetters,
                            ),
                        ).grid_index = update.location.index;
                        draft.currentClickIndex = null;
                        setConfirmedScorePoints(-1);
                    } else {
                        // we clicked a blank spot and we don't have a letter selected
                        // if it's the arrow, we'll add / mutate it
                        if (update.location.location === "GRID") {
                            // let's make sure there's no tile here
                            if (boardLetters[update.location.index] == null) {
                                // check if the arrow is present and here
                                if (
                                    draft.gridArrow != null &&
                                    draft.gridArrow.position ===
                                        update.location.index
                                ) {
                                    // arrow is present; cycle it
                                    if (draft.gridArrow.direction === "RIGHT") {
                                        draft.gridArrow.direction = "DOWN";
                                    } else if (
                                        draft.gridArrow.direction === "DOWN"
                                    ) {
                                        // remove arrow
                                        draft.gridArrow = null;
                                    }
                                } else {
                                    // arrow isn't present; set it
                                    draft.gridArrow = {
                                        direction: "RIGHT",
                                        position: update.location.index,
                                    };
                                }
                            }
                        }
                    }
                }
            } else if (update.$type === "NEW_LETTERS") {
                const tray = update.tray;
                const trayLength = Math.max(
                    draft.playerLetters.length,
                    tray.letters.length,
                );

                // we may need to check against the existing tray to retain whatever user reorderings there are
                const freeSpots = new Array<number | null>();
                for (let i = 0; i < trayLength; i++) {
                    freeSpots.push(i);
                }

                for (const letter of draft.playerLetters) {
                    if (letter != null && letter.grid_index == null) {
                        freeSpots[letter.tray_index] = null; // spot is taken
                    }
                }

                const firstNotNull = (): number => {
                    for (let i of freeSpots) {
                        if (i !== null) {
                            freeSpots[i] = null;
                            return i;
                        }
                    }

                    return null;
                };

                draft.playerLetters = tray.letters.map((newLetter, i) => {
                    // unchanged letters should share the same array index between draft.playerLetters and tray.letters
                    // This means that we want to re-use the spot's adjusted tray_index field, which is the visual index the player sees
                    if (newLetter != null) {
                        const currentLetter = draft.playerLetters[i];
                        newLetter["grid_index"] = null;

                        if (
                            currentLetter != null &&
                            currentLetter.grid_index == null
                        ) {
                            // Letter was in the tray; reuse its index
                            newLetter["tray_index"] = currentLetter.tray_index;
                        } else {
                            // letter was in the grid (or null though that shouldn't happen in practice)
                            // We have to find a free tray_index that isn't getting used
                            newLetter["tray_index"] = firstNotNull();
                        }
                    }
                    return newLetter as PlayableLetterData;
                });

                draft.gridArrow = null;
                draft.currentClickIndex = null;
            }
        });
    }

    return uiStateReducer;
}
